import React, { FC, useEffect, useMemo } from 'react';
import translate from '@translate';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { WebhookMethod, WebhookType } from 'skladon-types';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import TopBlock from './TopBlock';
import History from 'src/components/History';
import Loading from 'src/components/Loading/intex';
import { clearWebhookHistory, getWebhookHistoryFetch } from 'src/resources/webhookHistory/reducer';
import { ConstantsCodesObjectType } from 'src/types/constantsCodes';
import { historyUpdates } from './constants';
import { enrichGlobalWebhookHistoryForDisplay } from '../WebhookDetailContainer/functions';
import { TableRouterLink } from 'src/components/TableLink';
import { HistoryFormatedDataType } from 'src/components/History/types';
import { WebhookGlobalHistoryUpdateType } from 'src/types/webhookHistory';
import Typography from 'src/components/Typography';
import useWindowSize from 'src/utils/useWindowSize';

const WebhookHistoryListContainer: FC = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));
    const size = useWindowSize();

    const loading = useSelector((state: RootStoreType) => state.webhookHistory.loading);
    const historyData = useSelector((state: RootStoreType) => state.webhookHistory.data);
    const boolNumberCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.boolNumberCodes);

    const webhookTypeCodesObject = useMemo(
        () =>
            Object.values(WebhookType).reduce<ConstantsCodesObjectType>((acc, value) => {
                acc[value] = { value, label: translate(`webhooks.webhookType.${value}`) };
                return acc;
            }, {}),
        []
    );

    const methodCodesObject = useMemo(
        () =>
            Object.values(WebhookMethod).reduce<ConstantsCodesObjectType>((acc, value) => {
                acc[value] = { value, label: value };
                return acc;
            }, {}),
        []
    );

    const historyMaxHeight = useMemo(() => {
        if (!size.height) {
            return 640;
        }
        return size.height - (mobile ? 375 : 337);
    }, [size.height, mobile]);

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('webhookHistory.title') }));
        dispatch(getWebhookHistoryFetch());
        return () => {
            dispatch(clearWebhookHistory());
        };
    }, [dispatch]);

    const reloadHistory = () => {
        dispatch(getWebhookHistoryFetch());
    };

    const updates = useMemo(
        () => enrichGlobalWebhookHistoryForDisplay(historyData?.updates),
        [historyData?.updates],
    );

    const renderStepMeta = (item: HistoryFormatedDataType) => {
        const globalItem = item as WebhookGlobalHistoryUpdateType;
        const webhookId = globalItem.webhookId ?? globalItem.webhookContext?.webhookId;

        if (!webhookId) {
            return null;
        }

        return (
            <Typography variant="caption" display="block">
                <TableRouterLink to={`/settings/webhooks/${webhookId}/`}>
                    {translate('webhookHistory.webhookDetailLink', { id: webhookId })}
                </TableRouterLink>
            </Typography>
        );
    };

    return (
        <div data-testid="webhook-history-list">
            <TopBlock reloadHistory={reloadHistory} />
            {loading && <Loading />}
            <Box
                sx={{
                    maxHeight: historyMaxHeight,
                    overflow: 'auto',
                    pr: 2,
                }}
            >
                <History
                    updates={updates}
                    stepMeta={renderStepMeta}
                    defaultExpandAll
                    updateKeys={historyUpdates}
                    keyCodes={{
                        WEBHOOK_TYPE: webhookTypeCodesObject,
                        ACTIVE: boolNumberCodesObject,
                        METHOD: methodCodesObject,
                    }}
                    translatePrefix="webhookDetail"
                />
            </Box>
        </div>
    );
};

export default WebhookHistoryListContainer;
